import { useMemo, Fragment } from 'react';
import { NavContentBlueprint } from '@backstage/frontend-plugin-api';
import { NavContentComponentProps } from '@backstage/frontend-plugin-api';
import { Link } from 'react-router-dom';
import { AppIcons, resolveIcon } from '../utils/icons';
import LogoFull from '../components/Root/LogoFull';
import { NavGroup } from '../components/navigation/NavGroup';
import { createDebugLogger } from '../utils/debug';
import { useNavigationConfig } from '../hooks/useNavigationConfig';
import { DEFAULT_STANDALONE_CONFIG, DEFAULT_UTILITY_CONFIG } from '../utils/navigationDefaults';
import { buildCatalogUrl } from '../utils/catalog';
import { resolveStandaloneItem, resolveUtilityItem, ResolvedItem } from '../utils/navigationResolvers';

const logger = createDebugLogger('CustomNavContent');

const CustomNavContentComponent = ({ items }: NavContentComponentProps) => {
  const config = useNavigationConfig();

  const { navGroups, resolvedStandalone, resolvedUtility } = useMemo(() => {
    logger.log('Processing navigation with configuration', config);
    logger.time('nav-content-processing');

    // Process navigation groups with resolved URLs and icons
    const processedGroups =
      config.groups?.map(group => ({
        ...group,
        icon: resolveIcon(group.icon),
        items: group.items.map(item => ({
          ...item,
          to: item.to || buildCatalogUrl(item.filter),
          icon: resolveIcon(item.icon),
        })),
      })) || [];

    // Sort by priority
    processedGroups.sort((a, b) => a.priority - b.priority);

    // Process standalone items with config or zero-config defaults
    const isFromUserConfig = config.standalone !== undefined;
    const standaloneConfig = config.standalone || DEFAULT_STANDALONE_CONFIG;
    const standalone = standaloneConfig
      .map(item => resolveStandaloneItem(item, items, isFromUserConfig))
      .filter((item): item is ResolvedItem => item !== null)
      .sort((a, b) => a.priority - b.priority);

    // Process utility items with config or zero-config defaults
    const utilityConfig = config.utility || DEFAULT_UTILITY_CONFIG;
    const utility = utilityConfig
      .map(item => resolveUtilityItem(item))
      .filter((item): item is ResolvedItem => item !== null)
      .sort((a, b) => a.priority - b.priority);

    logger.timeEnd('nav-content-processing');
    logger.log(
      `Processed ${processedGroups.length} groups, ${standalone.length} standalone, ${utility.length} utility`,
    );

    return { navGroups: processedGroups, resolvedStandalone: standalone, resolvedUtility: utility };
  }, [config, items]);

  // Render the navigation bar
  return (
    <nav className="bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and Main Navigation */}
          <div className="flex">
            {/* Appvia Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <LogoFull />
              </Link>
            </div>

            {/* Business Navigation */}
            <div className="hidden sm:ml-8 sm:flex sm:space-x-1">
              {/* Render home nav item directly */}
              <Link
                to="/"
                className="inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-900 hover:text-gray-700 hover:border-gray-300 transition-colors duration-200"
              >
                <span className="mr-1.5 inline-flex items-center justify-center w-4 h-4">
                  <AppIcons.home fontSize="small" />
                </span>
                My dashboard
              </Link>

              {/* Render nav groups */}
              {navGroups.map(group => (
                <NavGroup
                  key={group.title}
                  title={group.title}
                  icon={group.icon}
                  items={group.items}
                  priority={group.priority}
                />
              ))}

              {/* Render standalone items dynamically */}
              {resolvedStandalone.map(item => (
                <Link
                  key={item.id}
                  to={item.to}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium border-b-2 border-transparent text-gray-900 hover:text-gray-700 hover:border-gray-300 transition-colors duration-200"
                >
                  {item.icon && (
                    <span className="mr-1.5 inline-flex items-center justify-center w-4 h-4">
                      <item.icon />
                    </span>
                  )}
                  {item.title}
                </Link>
              ))}
            </div>
          </div>

          {/* Utility Navigation */}
          <div className="hidden sm:flex sm:items-center sm:space-x-2">
            <div
              className="flex items-center px-3 py-1 rounded-lg border"
              style={{ backgroundColor: 'var(--appvia-background-default)' }}
            >
              {/* Render utility items dynamically */}
              {resolvedUtility.map((item, index) => (
                <Fragment key={item.id}>
                  <Link
                    to={item.to}
                    className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-600 hover:text-gray-900 border border-transparent transition-colors duration-200"
                    style={{
                      backgroundColor: 'transparent',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {item.icon && (
                      <span className="mr-1.5 inline-flex items-center justify-center w-4 h-4">
                        <item.icon fontSize="small" />
                      </span>
                    )}
                    {item.title}
                  </Link>
                  {/* Add divider between items (but not after last item) */}
                  {index < resolvedUtility.length - 1 && (
                    <div className="mx-2 h-4 w-px bg-gray-300" />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Create the NavContentBlueprint extension
export const customNavContent = NavContentBlueprint.make({
  name: 'custom-nav-content',
  params: {
    component: CustomNavContentComponent,
  },
});

// Export as appviaNavContent for use in appvia-styles package
export const appviaNavContent = customNavContent;
